import React from "react";
import { motion } from "framer-motion";
import { Award, Film, Code2, Rocket, Heart, Zap } from "lucide-react";
import { siteMeta, stats } from "../data/mock";
import MerakiSoulStation from "./MerakiSoulStation";
import AnimatedCounter from "./ui/AnimatedCounter";

// Meraki Essence — pseudo-3D workstation scene representing our craft
const Founder = () => {
  return (
    <section
      id="founder"
      className="section relative"
      style={{ background: "linear-gradient(180deg, #0a0908 0%, #14110f 50%, #0a0908 100%)" }}
    >
      <div className="container-xl">
        <SectionKicker index="02" label="The Meraki Soul" />

        <div className="grid lg:grid-cols-[1.15fr_1fr] gap-16 mt-14 items-center">
          {/* 3D workstation scene */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 30 }}
            whileInView={{ opacity: 1, scale: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <MerakiSoulStation />
          </motion.div>

          {/* Text Content: Meraki Summary */}
          <div>
            <motion.h3
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9 }}
              className="font-display text-[clamp(36px,5vw,68px)] leading-[1.02] text-[var(--cream)] balance"
            >
              Stories crafted. <span className="gold-text italic">Brands engineered.</span>
            </motion.h3>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.1 }}
              className="mt-8 p-6 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm"
            >
              <div className="flex items-center gap-3 mb-4">
                <Heart size={18} className="text-[var(--gold)]" />
                <span className="font-impact text-sm tracking-[0.2em] text-[var(--gold)] uppercase">Definition</span>
              </div>
              <p className="text-[18px] font-display italic text-[var(--cream)] leading-relaxed">
                "Meraki" — To do something with soul, creativity, or love; when you leave a piece of yourself in your work.
              </p>
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.15 }}
              className="mt-8 text-[16px] text-[var(--cream-muted)] leading-relaxed max-w-xl"
            >
              {siteMeta.description}
            </motion.p>

            <div className="mt-6 space-y-4 max-w-xl">
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-[14px] leading-relaxed text-[var(--muted)]"
              >
                At Meraki Solutions, we believe that the best work lives at the intersection of cinematic artistry and digital rigour. We are a multi-disciplinary collective built to solve complex problems through beautiful storytelling.
              </motion.p>
            </div>

            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((s, i) => (
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.3 + i * 0.1 }}
                >
                  <div className="font-impact text-4xl text-[var(--gold)]">
                    <AnimatedCounter value={parseInt(s.value)} suffix={s.suffix} />
                  </div>
                  <div className="text-[9px] uppercase tracking-[0.2em] text-[var(--muted)] mt-1">{s.label}</div>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="mt-12 w-full"
            >
              <a
                href="/careers"
                className="btn-primary w-full text-center block"
                data-cursor="start"
              >
                Work with us
              </a>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

const SectionKicker = ({ index, label }) => (
  <div className="flex items-center gap-4">
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--gold)]">{index}</span>
    <span className="w-16 h-px bg-[var(--rule)]" />
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--cream-muted)]">
      {label.toUpperCase()}
    </span>
  </div>
);

export default Founder;
